import React, { useMemo } from 'react';
import { FileText } from 'lucide-react';
import { jsonrepair } from 'jsonrepair';

/* ═══════════════════════════════════════════════════
   Types — matches GET /api/concept-test-vitamin/{id}
   ═══════════════════════════════════════════════════ */

interface KScore {
  mean: number;
  pct: number;
  std: number;
  benchmark: number;
  delta: number;
  verdict: 'pass' | 'warn' | 'fail';
  distribution?: Record<string, number>;
  n?: number;
}

interface SegmentData {
  name: string;
  description: string;
  n_agents: number;
  metrics: Record<string, KScore>;
  top_strength: { metric: string; label: string };
  primary_friction: { metric: string; label: string };
}

interface FrictionItem {
  rank: number;
  name: string;
  pct: number;
  count: number;
}

interface DriverItem {
  rank: number;
  name: string;
  pct: number;
  count: number;
  bar_width?: number;
}

interface InsightCard {
  headline: string;
  body: string;
}

interface VitaminReport {
  test_id: string;
  concept: Record<string, any>;
  population: string;
  n_agents: number;
  timestamp: string;
  overall_composite_pct: number;
  k_scores: Record<string, KScore>;
  segments: Record<string, SegmentData>;
  friction_drivers: {
    frictions: FrictionItem[];
    drivers: DriverItem[];
    quotes?: string[];
  };
  agent_results?: Record<string, any>;
  generated?: {
    verdict_text: string;
    segment_insights: Record<string, string>;
    friction_driver_descriptions: Record<string, string>;
    overall_insights: {
      cards: InsightCard[];
      strongest_signal?: string;
      biggest_risk?: string;
    };
  };
  _config?: Record<string, any>;
}

/* ═══════════════════════════════════════════════════
   Brand palette (BluePill)
   ═══════════════════════════════════════════════════ */

const BRAND = {
  accent: '#07E5D1',
  accentLight: '#E8FCFA',
  accentHover: '#06D4C2',
  dark: '#012e2a',
};

const K_LABELS: Record<string, string> = {
  k1: 'Purchase Intent',
  k2: 'Value Perception',
  k3: 'New & Different',
  k4: 'Believability',
};

const K_ORDER = ['k1', 'k2', 'k3', 'k4'];

const VERDICT_STYLE: Record<string, { bg: string; border: string; textClass: string; label: string; dotClass: string }> = {
  pass: { bg: 'bg-teal-50', border: 'border-teal-200', textClass: 'text-teal-700', label: 'above category norm', dotClass: 'bg-teal-500' },
  warn: { bg: 'bg-amber-50', border: 'border-amber-200', textClass: 'text-amber-700', label: 'at category norm — watch', dotClass: 'bg-amber-500' },
  fail: { bg: 'bg-red-50', border: 'border-red-200', textClass: 'text-red-700', label: 'below category norm', dotClass: 'bg-red-500' },
};

/* ═══════════════════════════════════════════════════
   Parse helper
   ═══════════════════════════════════════════════════ */

function parseReport(raw: any): VitaminReport | null {
  if (!raw) return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(jsonrepair(raw)) : raw;
    if (data.k_scores && data.overall_composite_pct !== undefined) return data;
    if (data.results && data.results.k_scores) return data.results;
    return data;
  } catch {
    return null;
  }
}

/* ═══════════════════════════════════════════════════
   Section Label
   ═══════════════════════════════════════════════════ */

const SectionLabel: React.FC<{ num: string; label: string }> = ({ num, label }) => (
  <div className="flex items-center gap-3 mb-7">
    <span className="text-xs font-bold tracking-wider font-mono tabular-nums" style={{ color: BRAND.accent }}>{num}</span>
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: BRAND.dark }}>{label}</span>
    <div className="flex-1 h-px" style={{ backgroundColor: `${BRAND.accent}30` }} />
  </div>
);

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */

interface Props {
  simulationResponse: any;
  contentData: any;
  isPreparingExport?: boolean;
}

const VitaminConceptTestResults: React.FC<Props> = ({ simulationResponse, contentData }) => {
  const report = useMemo(() => parseReport(simulationResponse), [simulationResponse]);
  const displayName = (contentData?.name as string) || report?.concept?.brand_name || 'Concept Report';

  if (!report || report.overall_composite_pct === undefined) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-3 text-gray-400" />
        <p>Unable to parse vitamin concept test results.</p>
      </div>
    );
  }

  const kKeys = K_ORDER.filter(k => report.k_scores[k]);
  const segEntries = Object.entries(report.segments || {})
    .map(([id, seg]) => ({ id, ...seg }))
    .sort((a, b) => (b.metrics?.k1?.pct ?? 0) - (a.metrics?.k1?.pct ?? 0));

  return (
    <div className="space-y-10">
      <CoverSection report={report} displayName={displayName} />
      <ExecSummary report={report} kKeys={kKeys} />
      {segEntries.length > 0 && <SegmentBreakdown report={report} kKeys={kKeys} segEntries={segEntries} />}
      <FrictionAndDrivers report={report} />
      {report.generated?.overall_insights?.cards && <OverallInsights report={report} />}

      {/* Report footer */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ backgroundColor: BRAND.dark }}
      >
        <div className="px-8 py-4 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-white/60">BluePill AI</span>
          <span className="text-[11px] text-white/35">
            {report.concept?.brand_name || 'Brand'} — CPG Concept Test — {report.test_id ? `BP-VIT-${report.test_id.slice(0, 6)}` : ''} — Confidential
          </span>
          <span className="text-[10px] text-white/25">bluepillai.com</span>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   COVER
   ═══════════════════════════════════════════════════ */

const CoverSection: React.FC<{ report: VitaminReport; displayName: string }> = ({ report, displayName }) => {
  const segCount = Object.keys(report.segments || {}).length;
  const stats = [
    { value: `N=${report.n_agents}`, label: 'Consumer twins' },
    { value: '1', label: 'Concepts tested' },
    { value: String(segCount), label: 'Segments' },
    { value: String(Object.keys(report.k_scores || {}).length), label: 'Core metrics' },
  ];

  return (
    <div className="rounded-lg shadow-sm overflow-hidden relative" style={{ backgroundColor: BRAND.dark }}>
      {/* Decorative arcs */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 600, height: 600, borderRadius: '50%',
          border: `1px solid ${BRAND.accent}12`,
          top: -200, right: -150,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 400, height: 400, borderRadius: '50%',
          border: `1px solid ${BRAND.accent}08`,
          top: -80, right: -50,
        }}
      />

      <div className="px-8 pt-8 pb-6 relative z-10">
        {/* Header: badge + meta */}
        <div className="flex items-start justify-between mb-10">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.09em] px-3 py-1.5 rounded-full"
              style={{ backgroundColor: BRAND.accent, color: BRAND.dark }}
            >
              BluePill AI
            </span>
            {report.concept?.brand_name && (
              <span className="text-xs text-white/40 tracking-wide">× {report.concept.brand_name}</span>
            )}
          </div>
          <div className="text-[11px] text-white/35 text-right leading-relaxed">
            {report.timestamp && <>Prepared: {new Date(report.timestamp).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}<br /></>}
            {report.test_id && <>Study ID: BP-VIT-{report.test_id.slice(0, 6)}<br /></>}
            Version: 1.0 — Final
          </div>
        </div>

        {/* Title block */}
        <div className="mb-12">
          <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-4">CPG Concept Test Report</div>
          <h2 className="text-3xl font-semibold text-white leading-tight mb-3">
            Concept Performance<br />
            <em className="font-normal text-white/40">& Consumer Response</em>
          </h2>
          <p className="text-sm text-white/50 leading-relaxed max-w-2xl">
            Testing: <strong className="text-white/80">{displayName}</strong>
            {report.concept?.product_headline && (
              <> — {report.concept.product_headline}</>
            )}
          </p>
        </div>

        {/* Stats row */}
        <div className="flex gap-12 pt-6" style={{ borderTop: `1px solid ${BRAND.accent}18` }}>
          {stats.map(s => (
            <div key={s.label}>
              <div className="text-4xl font-bold leading-none mb-1 tabular-nums" style={{ color: BRAND.accent }}>{s.value}</div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer note */}
      <div className="px-8 py-3 flex items-center justify-between gap-4 relative z-10" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-[10px] text-white/25 leading-relaxed">
          Produced using BluePill AI&rsquo;s synthetic consumer twin methodology. All responses are AI-simulated personas calibrated to behavioral, attitudinal, and category-specific shopper data.
        </p>
        <span className="text-[9px] uppercase tracking-wider text-white/20 font-semibold whitespace-nowrap">Confidential</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 01 — Executive Summary & Overall Score
   ═══════════════════════════════════════════════════ */

const ExecSummary: React.FC<{ report: VitaminReport; kKeys: string[] }> = ({ report, kKeys }) => {
  const ks = report.k_scores;

  return (
    <div>
      <SectionLabel num="01" label="Executive Summary & Overall Score" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Overall concept <span className="italic font-normal" style={{ color: BRAND.accent }}>performance.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Top-line verdict across all segments and metrics before breaking down by individual dimension and audience.
        </p>
      </div>

      {/* Hero row: main score + verdict chips */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4 mt-6 mb-6">
        {/* Main score card */}
        <div className="text-white rounded-lg shadow-sm p-8 flex flex-col justify-between min-h-[280px]" style={{ backgroundColor: BRAND.dark }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-white/45 mb-3">Overall Concept Score</div>
            <div className="text-7xl font-bold leading-none mb-2 tabular-nums" style={{ color: BRAND.accent }}>
              {report.overall_composite_pct}%
            </div>
            <div className="text-xs text-white/45 mb-6">composite across all four metrics</div>
          </div>
          {report.generated?.verdict_text && (
            <div
              className="text-sm leading-relaxed text-white/70 pt-4"
              style={{ borderTop: `1px solid ${BRAND.accent}25` }}
            >
              {report.generated.verdict_text}
            </div>
          )}
        </div>

        {/* Verdict chips */}
        <div className="flex flex-col gap-3">
          {kKeys.map(k => {
            const score = ks[k];
            if (!score) return null;
            return (
              <div key={k} className="rounded-lg border border-gray-200 bg-white p-4 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-[0.08em] mb-1" style={{ color: BRAND.dark }}>
                  {K_LABELS[k] || k}
                </div>
                <div className="text-3xl font-bold leading-none text-gray-900 tabular-nums">{score.pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 02 — Segment Breakdown
   ═══════════════════════════════════════════════════ */

interface SegEntry extends SegmentData { id: string; }

const SegmentBreakdown: React.FC<{ report: VitaminReport; kKeys: string[]; segEntries: SegEntry[] }> = ({ report, kKeys, segEntries }) => {
  const best = segEntries[0];
  const worst = segEntries[segEntries.length - 1];

  const piGap = best && worst
    ? Math.abs((best.metrics?.k1?.pct ?? 0) - (worst.metrics?.k1?.pct ?? 0))
    : 0;

  const weakestK = kKeys.reduce((wk, k) => {
    const curr = report.k_scores[k]?.pct ?? 100;
    const prev = report.k_scores[wk]?.pct ?? 100;
    return curr < prev ? k : wk;
  }, kKeys[0]);

  return (
    <div>
      <SectionLabel num="02" label="Segment Breakdown" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          How each segment <span className="italic font-normal" style={{ color: BRAND.accent }}>responded.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Segments ranked by overall appeal. &ldquo;Strength&rdquo; shows the highest-scoring metric per segment; &ldquo;primary friction&rdquo; shows where resistance concentrated.
        </p>
      </div>

      {/* Top-line cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 mb-8">
        <div className="rounded-lg shadow-sm p-5" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}30` }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: BRAND.dark }}>Highest Appeal Segment</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">{best?.name || '—'}</div>
          <div className="text-xs" style={{ color: `${BRAND.dark}88` }}>{best?.metrics?.k1?.pct ?? '—'}% purchase intent</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Biggest PI Gap Across Segments</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">+{piGap} pts</div>
          <div className="text-xs text-gray-400">{best?.name || '—'} vs. {worst?.name || '—'}</div>
        </div>
        <div className="rounded-lg shadow-sm p-5 text-white" style={{ backgroundColor: BRAND.dark }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1">Weakest Metric Overall</div>
          <div className="text-xl font-bold leading-tight mb-1" style={{ color: BRAND.accent }}>{K_LABELS[weakestK] || weakestK}</div>
          <div className="text-xs text-white/40">lowest across segments</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: BRAND.dark }}>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60 w-8">#</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Segment</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Overall</th>
                {kKeys.map(k => (
                  <th key={k} className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">
                    {(K_LABELS[k] || k).replace('Purchase Intent', 'PI').replace('Value Perception', 'VP').replace('New & Different', 'N&D').replace('Believability', 'Bel.')}
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Top Strength</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Primary Friction</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Key Insight</th>
              </tr>
            </thead>
            <tbody>
              {segEntries.map((seg, idx) => {
                const metricValues = kKeys.map(k => seg.metrics?.[k]?.pct ?? 0);
                const overallAppeal = metricValues.length > 0
                  ? Math.round(metricValues.reduce((a, b) => a + b, 0) / metricValues.length)
                  : 0;
                const segInsight = report.generated?.segment_insights?.[seg.id] || '';

                return (
                  <tr key={seg.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors align-top">
                    <td className="px-3 py-4 text-xs text-gray-400 font-mono tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-4 max-w-[200px]">
                      <div className="font-semibold text-gray-900 text-[13px]">{seg.name}</div>
                      {seg.description && <p className="text-[11px] text-gray-500 leading-snug mt-1 line-clamp-2">{seg.description}</p>}
                      <div className="text-[10px] text-gray-400 mt-0.5">{seg.n_agents} agents</div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="text-lg font-bold leading-none mb-1 tabular-nums" style={{ color: BRAND.dark }}>{overallAppeal}%</div>
                      <div className="w-14 h-[3px] bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(overallAppeal, 100)}%`, backgroundColor: BRAND.accent }} />
                      </div>
                    </td>
                    {kKeys.map(k => {
                      const m = seg.metrics?.[k];
                      if (!m) return <td key={k} className="px-3 py-4 text-sm text-gray-300">—</td>;
                      const vs = VERDICT_STYLE[m.verdict] || VERDICT_STYLE.warn;
                      return (
                        <td key={k} className="px-3 py-4">
                          <div className="text-lg font-bold text-gray-900 leading-none mb-1 tabular-nums">{m.pct}%</div>
                          <div className="w-14 h-[3px] bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${vs.dotClass}`} style={{ width: `${Math.min(m.pct, 100)}%` }} />
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-4">
                      {seg.top_strength?.label && (
                        <span
                          className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${BRAND.accent}20`, color: BRAND.dark }}
                        >
                          {seg.top_strength.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      {seg.primary_friction?.label && (
                        <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                          {seg.primary_friction.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4 max-w-[200px]">
                      {segInsight && (
                        <p className="text-[11px] text-gray-600 leading-snug">{segInsight}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 03 — Friction & Purchase Drivers
   ═══════════════════════════════════════════════════ */

const FrictionAndDrivers: React.FC<{ report: VitaminReport }> = ({ report }) => {
  const fd = report.friction_drivers || { frictions: [], drivers: [] };
  const frictions = fd.frictions || [];
  const drivers = fd.drivers || [];
  const fdd = report.generated?.friction_driver_descriptions || {};

  return (
    <div>
      <SectionLabel num="03" label="Friction & Purchase Drivers" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What stopped them, <span className="italic font-normal" style={{ color: BRAND.accent }}>and what pulled them in.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Primary friction categories and purchase drivers ranked by frequency of mention across all segments. Percentages reflect share of respondents surfacing each theme.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Friction section */}
        <div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-5 pb-3"
            style={{ color: BRAND.dark, borderBottom: `2px solid ${BRAND.accent}30` }}
          >
            Primary Friction Categories
          </div>
          <div className="space-y-1">
            {frictions.map(f => (
              <div key={f.rank} className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
                <div className="text-2xl font-bold leading-none min-w-[28px] tabular-nums" style={{ color: `${BRAND.accent}40` }}>
                  {f.rank}
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-gray-900 mb-1">{f.name}</div>
                  {fdd[f.name] && (
                    <p className="text-[11px] text-gray-500 leading-relaxed mb-1.5">{fdd[f.name]}</p>
                  )}
                  <div className="text-[10px] text-gray-400 italic">Mentioned by {f.pct}% of consumers</div>
                </div>
                <div className="text-xl font-bold text-red-600 leading-none min-w-[48px] text-right tabular-nums">{f.pct}%</div>
              </div>
            ))}
            {frictions.length === 0 && (
              <div className="text-sm text-gray-400 italic py-4">No friction data available</div>
            )}
          </div>
        </div>

        {/* Driver section */}
        <div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-5 pb-3"
            style={{ color: BRAND.dark, borderBottom: `2px solid ${BRAND.accent}30` }}
          >
            Primary Purchase Drivers
          </div>
          <div className="space-y-1">
            {drivers.map(d => (
              <div key={d.rank} className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
                <div className="text-2xl font-bold leading-none min-w-[28px] tabular-nums" style={{ color: `${BRAND.accent}40` }}>
                  {d.rank}
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-gray-900 mb-1">{d.name}</div>
                  {fdd[d.name] && (
                    <p className="text-[11px] text-gray-500 leading-relaxed mb-1.5">{fdd[d.name]}</p>
                  )}
                  <div className="h-[4px] bg-gray-100 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.min(d.bar_width ?? d.pct, 100)}%`, backgroundColor: BRAND.accent }}
                    />
                  </div>
                </div>
                <div className="text-xl font-bold leading-none min-w-[48px] text-right tabular-nums" style={{ color: BRAND.dark }}>
                  {d.pct}%
                </div>
              </div>
            ))}
            {drivers.length === 0 && (
              <div className="text-sm text-gray-400 italic py-4">No driver data available</div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 04 — Overall Insights
   ═══════════════════════════════════════════════════ */

const OverallInsights: React.FC<{ report: VitaminReport }> = ({ report }) => {
  const oi = report.generated?.overall_insights;
  if (!oi) return null;

  const cards = oi.cards || [];

  return (
    <div>
      <SectionLabel num="04" label="Overall Insights" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Key findings <span className="italic font-normal" style={{ color: BRAND.accent }}>and recommendations.</span>
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {cards.map((card, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: BRAND.accent }} />
            <div className="text-4xl font-bold leading-none mb-3 tabular-nums" style={{ color: `${BRAND.accent}40` }}>
              {idx + 1}
            </div>
            <div className="text-[13px] font-semibold text-gray-900 leading-snug mb-2">{card.headline}</div>
            <p className="text-[11.5px] text-gray-500 leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>

      {(oi.strongest_signal || oi.biggest_risk) && (
        <div className="rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-6" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}35` }}>
          {oi.strongest_signal && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.09em] mb-2" style={{ color: BRAND.dark }}>Strongest Signal</div>
              <p className="text-[13px] text-gray-900 leading-relaxed">{oi.strongest_signal}</p>
            </div>
          )}
          {oi.biggest_risk && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.09em] text-red-600 mb-2">Biggest Risk</div>
              <p className="text-[13px] text-gray-900 leading-relaxed">{oi.biggest_risk}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VitaminConceptTestResults;
